import { AxiosResponse } from 'axios'
import { Component } from 'preact'
import { checkAttachments } from './utils/checkAttachments'

import Editor from './index.js'
import redirect from '../../utils/redirect'
import Phrase from '../../libs/translationsUtils/phrase'
import { ContentType } from '@insided/shared/utils/contentType'
import { isSpam } from '@insided/shared/services/spamService'
import { editReply, replyToTopic } from '@insided/shared/services/topicService'

type PostRequestResponse = AxiosResponse<{
  errors: string[]
  success: string | undefined
}>
type PostRequest = (content: string, attachments: Array<{}>, hasFileInProgress: boolean) => Promise<PostRequestResponse>

function newPost (topicId: number, type: ContentType): PostRequest {
  return async (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return await replyToTopic(type, topicId, content)
  }
}

function editPost (topicId: number, replyId: number, type: ContentType): PostRequest {
  return async (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return await editReply(type, replyId, content)
  }
}

function determineRequest ({ replyId, topicId, type }: {replyId: number | null, isControl: boolean, topicId: number, type: ContentType}) {
  if (replyId !== null) return editPost(topicId, replyId, type)
  return newPost(topicId, type)
}

async function checkIsSpam (replyId: number, type: ContentType, isReply: boolean): Promise<boolean> {
  try {
    const response = await isSpam(replyId, type, isReply)
    return response.data.result.isSpam as boolean
  } catch (e) {
    return await new Promise((resolve) => {
      resolve(false)
    })
  }
}

interface Props {
  replyId: number | null
  topicId: number
  isControl: boolean
  redirect: boolean
  autoFocus?: boolean
  isGuest?: boolean
  onSuccess: (content: string, response: PostRequestResponse) => void
  onCancel?: () => void
  disableFileAttachments: boolean
  data?: string
  type: ContentType
}

interface State {
  attachments: Array<{}>
  errors: Array<{errorMessage: string}>
  isLoading: boolean
}

function getReplyIdFromResponse (response: AxiosResponse) {
  const { location } = response.headers
  const match = /replies\/(\d+)/.exec(location)
  return match ? Number(match[1]) : null
}

class postEditorViaAPI extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      attachments: [],
      errors: [],
      isLoading: false
    }
  }

  render () {
    const props = this.props
    const submitRequest = determineRequest(props)
    return (
      <div>
        <Editor
          {...props}
          disableFileAttachments={props.disableFileAttachments}
          isControl={props.isControl}
          autoFocus={!!props.autoFocus}
          errors={this.state.errors}
          isLoading={this.state.isLoading}
          attachments={this.state.attachments}
          onSubmit={async (content: string, attachments: Array<{}>, hasFileInProgress: boolean) => {
            this.setState({
              isLoading: true
            })
            try {
              const response = await submitRequest(content, attachments.map(item => JSON.stringify(item)), hasFileInProgress)

              const errors = response.data.errors
              if (errors?.length) {
                const newErrors = errors.map(error => {
                  return { errorMessage: error }
                })
                this.setState({
                  errors: this.state.errors.concat(newErrors),
                  isLoading: false
                })
                return
              }

              const replyId = props.replyId ?? getReplyIdFromResponse(response)
              if (replyId) {
                const isSpam = await checkIsSpam(replyId, props.type, true)
                // is-pending header is 1 if reply is pending, 0 if not pending
                const isPending = Boolean(Number(response.headers['is-pending']))
                if (isSpam || isPending) {
                  const errorMessage = <Phrase m='Forum' p='spam_moderation.reviewbymoderator.message' /> as unknown as string
                  this.setState({
                    errors: this.state.errors.concat({
                      errorMessage
                    }),
                    isLoading: false
                  })
                  return
                }
              }

              if (props.redirect) {
                // This part needs to be updated when we have redirect to post
                return redirect(response.data.success ?? response.headers.location)
              }
              props.onSuccess(content, response)
              this.setState({ errors: [] })
              window.CKEDITOR.instances.editor1.setData('')
            } catch (error) {
              const errorMessage = error.response.data?.errors?.pagetext || error.response.data?.errors?.content || error.response.data?.errors || 'Something\'s gone wrong'

              this.setState({
                errors: this.state.errors.concat({
                  errorMessage
                })
              })
            }
            this.setState({
              isLoading: false
            })
          }}
        />
      </div>
    )
  }
}

export default postEditorViaAPI
